import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface CreateTranscriptParams {
  caseId: string;
  name?: string;
  objective?: string;
  deponent?: string;
  otherSpeakers?: string;
  phraseList?: string;
}

interface CreateTranscriptResponse {
  id: string;
  name: string;
  caseId: string;
  status: string;
  createdDate: string;
}

export const useCreateTranscript = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<CreateTranscriptResponse, Error, CreateTranscriptParams>(
    async (params: CreateTranscriptParams) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          name: params.name ?? `Recording ${new Date().toLocaleString()}`,
          objective: params.objective,
          deponent: params.deponent,
          otherSpeakers: params.otherSpeakers
            ? params.otherSpeakers
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s)
            : undefined,
          phraseList: params.phraseList
            ? params.phraseList
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s)
            : undefined,
        },
      });

      const response = await fetch(`${APIBaseChronos}/api/case/${params.caseId}/transcripts`, fetchConfig);

      if (!response.ok) {
        throw new Error('Failed to create transcript');
      }

      return response.json();
    },
    {
      onSuccess: (data: CreateTranscriptResponse) => {
        queryClient.invalidateQueries([QueryEntity.Transcripts, { caseId: data.caseId }]);
      },
      onError: () => {
        toast.error('Failed to create transcript');
      },
    },
  );
};
